import React, { useEffect, useState } from 'react';
import {
  DEFAULT_GRADE, PRESETS, clone, cubeText, baseGrade, applyLook, neutralLook,
  gradeSignature, stampPreset, blendGrade, activeAdjustments, activeCurveNotes,
} from '../utils/grade';
import ToneCurve from './ToneCurve';
import ColorWheel from './ColorWheel';
import SITE from '../site';

// The neutral, decode-only baseline for a grade (utils/grade neutralLook):
// strips every creative move back to DEFAULT_GRADE but keeps the camera de-log
// essentials (input transform, auto-exposure, output encoding) on a NEUTRAL
// profile so RED footage stays viewable without any residual S-curve. This is
// both the "Reset" target and the 0%-strength end of the preset-amount blend.
const neutralDecode = neutralLook;

const BAND_NAMES = ['Red', 'Orange', 'Yellow', 'Green', 'Aqua', 'Blue', 'Purple', 'Magenta'];
const BAND_COLORS = ['#e0484c', '#e07b2c', '#d9c024', '#4caf50', '#26c6da', '#3f6fff', '#7e57c2', '#d04ca0'];

const TONE_OPTS = ['off', 'low', 'medium', 'high'];
const ROLL_OPTS = ['off', 'low', 'medium', 'high'];
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// Deep-equality vs a default subtree, so a section's Reset (and each control's
// tiny ↺) can disable itself when nothing's changed.
function sameJson(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

// Inline "Auto: value · Apply" suggestion chip, shared by every control type.
// `current` drives the "✓ applied" state (numeric with tolerance, everything
// else by JSON equality — selects, wheels {h,s,l}, curve point arrays / null).
// `sug.label` is an optional pretty display string; `sug.note` a short inline
// qualifier ("leave neutral", "starting point") — the full why stays on hover.
function Sug({ sug, current, tol = 0, render = null }) {
  if (!sug) return null;
  const display = sug.label != null
    ? sug.label
    : sug.text
      ? (render ? render(sug.value) : String(sug.value))
      : `${sug.value > 0 ? '+' : ''}${sug.value}${sug.unit ? ` ${sug.unit}` : ''}`;
  const at = typeof sug.value === 'number' && typeof current === 'number'
    ? Math.abs(current - sug.value) <= tol
    : sameJson(current ?? null, sug.value ?? null);
  return (
    <div className="cs-gp-sug" title={sug.why || ''}>
      <span className="cs-gp-sug-val">
        Auto: <b>{display}</b>
        {sug.note ? <i className="cs-gp-sug-note"> · {sug.note}</i> : null}
      </span>
      <button type="button" className="cs-gp-sug-apply" onClick={sug.onApply} disabled={at}>
        {at ? '✓ applied' : 'Apply'}
      </button>
    </div>
  );
}

// Tiny per-control reset — the discoverable version of double-click-to-default.
// Fades out (but keeps its slot, no layout shift) when the value is already at
// the default.
function ResetBtn({ atDef, onReset, what, defLabel }) {
  return (
    <button
      type="button"
      className="cs-gp-rst"
      disabled={atDef}
      title={atDef ? `${what} is at its default` : `Reset ${what} to ${defLabel}`}
      onClick={onReset}
    >
      ↺
    </button>
  );
}

// Small segmented control for the discrete display-transform knobs. `sug`
// (optional, like Slider's) renders an "Auto: value · Apply" chip underneath;
// `def` (optional) adds the per-control ↺ reset.
function Seg({ label, value, options, onChange, render = cap, sug = null, def = null }) {
  return (
    <div className="cs-gp-seg-row">
      <span className="cs-gp-seg-lbl">{label}</span>
      <div className="cs-gp-seg">
        {options.map((o) => (
          <button
            key={o}
            type="button"
            className={value === o ? 'on' : ''}
            onClick={() => onChange(o)}
          >
            {render(o)}
          </button>
        ))}
      </div>
      {def != null && (
        <ResetBtn atDef={value === def} onReset={() => onChange(def)} what={label} defLabel={render(def)} />
      )}
      <Sug sug={sug} current={value} render={render} />
    </div>
  );
}

// Trigger a client-side .cube download (no backend round-trip).
function downloadCube(grade, name) {
  const text = cubeText(grade, 33, name);
  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${name.replace(/[^\w.-]+/g, '_')}.cube`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function Slider({ label, value, onChange, min = -100, max = 100, step = 1, def = 0, unit = '', sug = null }) {
  const tol = (step || 1) / 2;
  const atDef = Math.abs(Number(value) - Number(def)) < tol;
  return (
    <div className="cs-gp-slider">
      <div className="cs-gp-slabel">
        <span>{label}</span>
        <span className="cs-gp-slctl">
          <ResetBtn atDef={atDef} onReset={() => onChange(def)} what={label} defLabel={`${def}${unit ? ` ${unit}` : ''}`} />
          <input
            className="cs-gp-snum"
            type="number"
            value={value}
            min={min}
            max={max}
            step={step}
            onChange={(e) => onChange(Number(e.target.value))}
          />
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        onDoubleClick={() => onChange(def)}
        title={`Double-click to reset to ${def}`}
      />
      {unit ? <span className="cs-gp-unit">{unit}</span> : null}
      <Sug sug={sug} current={Number(value)} tol={sug && sug.text ? 0 : tol} />
    </div>
  );
}

function Section({ title, children, defaultOpen = false, onReset, canReset = true }) {
  const [open, setOpen] = useState(defaultOpen);
  // Switching the panel into stage mode flips `defaultOpen` on an already-
  // mounted section (the panel is NOT remounted), so without this the stage's
  // one section stays collapsed and its controls/reset look "missing".
  useEffect(() => { if (defaultOpen) setOpen(true); }, [defaultOpen]);
  return (
    <div className={`cs-gp-section ${open ? 'open' : ''}`}>
      <div className="cs-gp-head">
        <button type="button" className="cs-gp-head-toggle" onClick={() => setOpen((o) => !o)}>
          <span>{title}</span>
        </button>
        {onReset ? (
          <button
            type="button"
            className="cs-gp-head-reset"
            disabled={!canReset}
            title={canReset ? `Reset ${title}` : `${title} is already at default`}
            onClick={(e) => {
              e.stopPropagation();
              onReset();
            }}
          >
            Reset
          </button>
        ) : null}
        <button type="button" className="cs-gp-chev" onClick={() => setOpen((o) => !o)} aria-label="toggle">
          {open ? '\u2212' : '+'}
        </button>
      </div>
      {open ? <div className="cs-gp-body">{children}</div> : null}
    </div>
  );
}

// Lightroom/Lumetri-style grade editor. Emits onChange(grade) on every edit.
// `sections` (optional) limits the panel to a subset of control groups — used by
// the stage-by-stage workflow so each stage shows ONLY its own adjustments
// (decode / light / color / finish). When set, the preset/LUT/active-adjustment
// chrome is hidden and the listed sections render open.
export default function GradePanel({ grade, onChange, presets = [], onSavePreset, onDeletePreset, kind = 'photo', sections = null, variant = null, suggestions = null, isRed = false }) {
  const stageMode = Array.isArray(sections);
  const dock = variant === 'dock';
  const show = (id) => !stageMode || sections.includes(id);
  const showAny = (...ids) => !stageMode || ids.some((id) => sections.includes(id));
  // Inline auto-suggestion for the slider at `path` (keyed by dotted path in the
  // `suggestions` map). Returns the value/why plus an Apply handler so the chip
  // renders right under the matching control. Null when there's no suggestion.
  const sugFor = (path) => {
    if (!suggestions) return null;
    const s = suggestions[path.join('.')];
    if (!s) return null;
    return { ...s, onApply: () => set(path, s.value) };
  };
  const [band, setBand] = useState(0);
  // The applied preset is stamped on the grade itself (grade._preset), so it
  // persists across reloads / clip switches and shows up in the review tree. We
  // read it from the grade instead of local state.
  const applied = grade?._preset || null;

  const set = (path, val) => {
    const next = clone(grade);
    let obj = next;
    for (let i = 0; i < path.length - 1; i += 1) {
      const k = path[i];
      // Older stored grades can miss whole subtrees (e.g. `display`).
      if (obj[k] == null || typeof obj[k] !== 'object') obj[k] = {};
      obj = obj[k];
    }
    obj[path[path.length - 1]] = val;
    onChange(next);
  };

  // Stack the look on top of the current decode so display-referred styles don't
  // discard a camera-log de-log (RED Log3G10 etc.). Reset clears the creative
  // look but keeps the de-log, so RED footage stays viewable instead of flat.
  const applyPreset = (p) => {
    // Stamp the preset name/package onto the composed grade so the lineage is
    // preserved (review tree + reload), not just the raw slider values. Applied
    // at full strength; the Amount slider below dials it back per clip.
    onChange(stampPreset(applyLook(grade, p.grade), p, 100));
  };
  // Full reset: clear ALL creative grading — including the Display Transform
  // "light" knobs (tone map / roll-off) that presets like Tucker change — and go
  // back to the neutral de-logged baseline. We keep only the camera decode so the
  // image stays viewable: the input transform, the auto-exposure, and the output
  // encoding. Everything else returns to DEFAULT_GRADE. (The old reset preserved
  // the whole `display` object, so display/light edits silently survived a Reset.)
  const reset = () => onChange(neutralDecode(grade));
  // Reset one or more grade subtrees in a single change (so multi-field section
  // resets don't clobber each other through stale state). Paths may be a plain
  // key ('display') or an array (['curve', 'master']) — most call sites pass
  // plain keys, which used to be walked char-by-char and threw, so every
  // section Reset with a string path silently did nothing.
  const resetMany = (updates) => {
    const next = clone(grade);
    for (const [path, value] of updates) {
      const keys = Array.isArray(path) ? path : [path];
      let obj = next;
      for (let i = 0; i < keys.length - 1; i += 1) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = clone(value);
    }
    onChange(next);
  };
  const baseExposure = (grade?.display?.exposure ?? DEFAULT_GRADE.display.exposure);
  const baseOutput = (grade?.display?.output || DEFAULT_GRADE.display.output);
  // The neutral display transform for this asset (keeps decode essentials).
  const defaultDisplay = { ...clone(DEFAULT_GRADE.display), exposure: baseExposure, output: baseOutput };

  const savePreset = () => {
    const name = window.prompt('Preset name');
    if (name && onSavePreset) onSavePreset(name.trim(), stampPreset(grade, null));
  };

  const g = grade;
  const allPresets = [...PRESETS.filter((p) => p.id !== 'none'), ...presets];
  // Is the applied preset still exactly in place, or has the user edited it?
  const appliedExact = applied && gradeSignature(grade) === applied.sig;
  const appliedEdited = applied && !appliedExact;

  // Per-clip preset strength: blend the preset from its neutral, decode-only
  // baseline (0%) up to the full look (100%). Re-derives base/full from the
  // preset definition each time so the dial stays consistent across reloads.
  const presetDef = applied ? allPresets.find((p) => (p.id || p.name) === applied.id) : null;
  const presetAmount = applied?.amount ?? 100;
  const setPresetAmount = (amt) => {
    if (!presetDef) return;
    const base = neutralDecode(grade);
    const full = applyLook(base, presetDef.grade);
    onChange(stampPreset(blendGrade(base, full, amt / 100), presetDef, amt));
  };

  // Flat list of the line items currently doing something, for quick fine-tuning.
  const adjustments = activeAdjustments(grade);
  const adjNotes = activeCurveNotes(grade);

  // Show the input-science + display-transform controls for video, and also for
  // any still that carries a camera-log decode (e.g. a RED 16-bit frame grab).
  const isLog = !!g.inputTransform && g.inputTransform !== 'srgb';
  const showInput = kind === 'video' || isLog;
  // RED RAW sources get the full display-transform block even before a
  // transform is chosen — otherwise the decode stage shows nothing to set (no
  // log exposure, no way to apply the Log3G10 suggestions) until the user
  // somehow picks the transform first.
  const showDisplay = isLog || (isRed && kind === 'video');
  const disp = g.display || DEFAULT_GRADE.display;

  return (
    <div className={`cs-gradepanel${dock ? ' cs-gp--dock' : ''}`}>
      {!stageMode && (
      <>
      <div className="cs-gp-presets">
        <select
          className="cs-gp-presel"
          value={appliedExact ? applied.id : ''}
          onChange={(e) => {
            const p = allPresets.find((x) => (x.id || x.name) === e.target.value);
            if (p) applyPreset(p);
          }}
        >
          <option value="">{applied ? 'Apply another preset…' : 'Apply preset / style…'}</option>
          {PRESETS.filter((p) => p.id !== 'none').map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
          {presets.length ? <option disabled>──────────</option> : null}
          {presets.map((p) => (
            <option key={p.id} value={p.id}>{p.name}{p.kind && p.kind !== 'any' ? ` (${p.kind})` : ''}</option>
          ))}
        </select>
        <button type="button" className="cs-btn cs-btn-sm" onClick={savePreset}>Save</button>
        <button
          type="button"
          className="cs-btn cs-btn-sm cs-btn-ghost"
          onClick={reset}
          title="Clear ALL grading (color, light & display transform) back to the neutral de-logged baseline"
        >
          Reset all
        </button>
      </div>
      {applied && (
        <div className={`cs-gp-applied ${appliedEdited ? 'is-edited' : ''}`}>
          <span className="cs-gp-applied-dot" />
          {`${appliedExact ? 'Applied: ' : ''}${applied.name}`}
          {presetAmount < 100 ? ` · ${presetAmount}%` : ''}
          {appliedEdited ? ' · edited' : ''}
        </div>
      )}

      {applied && presetDef && (
        <div className="cs-gp-amount">
          <Slider
            label="Preset amount"
            value={presetAmount}
            onChange={setPresetAmount}
            min={0}
            max={100}
            step={1}
            def={100}
            unit="%"
          />
          <div className="cs-gp-hint">
            Dial the whole <b>{presetDef.name}</b> look back for this clip — 100% = full preset, 0% = off.
            Strong presets often sit best around 40–70% on a given shot. Fine-tune individual settings below.
          </div>
        </div>
      )}

      {applied && (
        <div className="cs-gp-calib">
          <div className="cs-gp-calib-head">
            <span className="cs-gp-calib-step">1</span>
            <span className="cs-gp-calib-title">Calibrate this clip first</span>
          </div>
          <div className="cs-gp-calib-sub">
            Presets bake in one clip’s exposure &amp; white balance. Dial these in for <b>this</b> clip
            before judging the creative look — then tweak the look below.
          </div>
          <Slider label="Exposure" value={g.basic.exposure} onChange={(v) => set(['basic', 'exposure'], v)} min={-5} max={5} step={0.05} def={0} unit="EV" />
          <Slider label="Temp" value={g.wb.temp} onChange={(v) => set(['wb', 'temp'], v)} def={0} />
          <Slider label="Tint" value={g.wb.tint} onChange={(v) => set(['wb', 'tint'], v)} def={0} />
        </div>
      )}

      <div className="cs-gp-luts">
        <span className="cs-gp-luts-lbl">Export LUT (.cube)</span>
        <button
          type="button"
          className="cs-btn cs-btn-sm cs-btn-ghost"
          onClick={() => downloadCube(g, `${SITE.id}-look`)}
          title="Full creative grade as a 33³ .cube for Resolve / Premiere / Photoshop"
        >
          Creative
        </button>
        {isLog ? (
          <button
            type="button"
            className="cs-btn cs-btn-sm cs-btn-ghost"
            onClick={() => downloadCube(baseGrade(g), `${SITE.id}-neutral-base`)}
            title="Display transform only (neutral base) — a clean Rec.709/sRGB negative to grade from"
          >
            Neutral base
          </button>
        ) : null}
      </div>

      <Section
        title={`Active adjustments${adjustments.length ? ` (${adjustments.length})` : ''}`}
        defaultOpen
        onReset={reset}
        canReset={adjustments.length > 0 || adjNotes.length > 0}
      >
        {adjustments.length ? (
          <>
            <div className="cs-gp-hint" style={{ marginBottom: 6 }}>
              Every setting currently doing something — whether from a preset or by hand. Tune or zero
              out any single line item here (double-click a slider to reset it).
            </div>
            {adjustments.map((a) => (
              <Slider
                key={a.key}
                label={a.label}
                value={a.value}
                onChange={(v) => set(a.path, v)}
                min={a.min}
                max={a.max}
                step={a.step}
                def={a.def}
                unit={a.unit}
              />
            ))}
          </>
        ) : (
          <div className="cs-gp-hint">No active adjustments — this clip is ungraded. Apply a style or tune the controls below.</div>
        )}
        {adjNotes.length ? (
          <div className="cs-gp-adjnotes">
            Also active: {adjNotes.join(', ')} — edit in their own controls below.
          </div>
        ) : null}
      </Section>
      </>
      )}

      {show('decode') && (() => {
        // The section's "no edit" profile: camera-log footage rests on the
        // NEUTRAL profile (pure de-log, no baked S-curve — matches neutralLook
        // and the None/Reset chips); display footage rests on the default.
        const restProfile = isLog ? 'neutral' : DEFAULT_GRADE.profile;
        // Stage mode: Reset undoes the WHOLE decode (input transform, display
        // transform incl. log exposure, profile) back to no-edit — the stage's
        // clear() semantics. Quick mode keeps the decode essentials so RED
        // footage stays viewable.
        const decodeUntouched =
          (g.inputTransform || 'srgb') === DEFAULT_GRADE.inputTransform &&
          sameJson({ ...DEFAULT_GRADE.display, ...disp }, DEFAULT_GRADE.display) &&
          g.profile === DEFAULT_GRADE.profile;
        return (
      <Section
        title={stageMode ? 'Decode' : 'Profile'}
        defaultOpen={stageMode}
        onReset={() => (stageMode
          ? resetMany([
            ['inputTransform', DEFAULT_GRADE.inputTransform],
            ['display', DEFAULT_GRADE.display],
            ['profile', DEFAULT_GRADE.profile],
          ])
          : resetMany([['display', defaultDisplay], ['profile', restProfile]]))}
        canReset={stageMode
          ? !decodeUntouched
          : (
            g.profile !== restProfile ||
            (disp.tonemap || 'medium') !== DEFAULT_GRADE.display.tonemap ||
            (disp.rolloff || 'off') !== DEFAULT_GRADE.display.rolloff
          )}
      >
        {(() => {
          const profileDef = stageMode ? DEFAULT_GRADE.profile : restProfile;
          const profileName = (p) => (p === 'adobe-color' ? 'Color' : p === 'neutral' ? 'Neutral' : 'Flat');
          return (
            <>
              <div className="cs-gp-profile">
                {['adobe-color', 'neutral', 'flat'].map((p) => (
                  <button
                    key={p}
                    type="button"
                    className={`cs-chip ${g.profile === p ? 'active' : ''}`}
                    onClick={() => set(['profile'], p)}
                  >
                    {profileName(p)}
                  </button>
                ))}
                <ResetBtn
                  atDef={g.profile === profileDef}
                  onReset={() => set(['profile'], profileDef)}
                  what="Profile"
                  defLabel={profileName(profileDef)}
                />
              </div>
              <Sug sug={sugFor(['profile'])} current={g.profile} render={profileName} />
            </>
          );
        })()}
        {showInput ? (
          <>
            <label className="cs-gp-it">
              Input color science
              <span className="cs-gp-itrow">
                <select value={g.inputTransform || 'srgb'} onChange={(e) => set(['inputTransform'], e.target.value)}>
                  <option value="srgb">Rec.709 / sRGB (no transform)</option>
                  <option value="log3g10">RED Log3G10 · Wide Gamut → 709 (IPP2)</option>
                  <option value="slog3">Sony S-Log3 → 709</option>
                  <option value="clog3">Canon C-Log3 → 709</option>
                  <option value="vlog">Panasonic V-Log → 709</option>
                  <option value="hlg">HLG → 709</option>
                  <option value="linear">Linear → 709</option>
                </select>
                <ResetBtn
                  atDef={(g.inputTransform || 'srgb') === 'srgb'}
                  onReset={() => set(['inputTransform'], 'srgb')}
                  what="Input color science"
                  defLabel="no transform (Rec.709/sRGB)"
                />
              </span>
            </label>
            <Sug sug={sugFor(['inputTransform'])} current={g.inputTransform || 'srgb'} />
            {isRed || isLog ? (
              <div className="cs-gp-hint">
                For flat/monotone RED footage pick <b>RED Log3G10</b> to de-log into a natural Rec.709
                base, then grade from there.
              </div>
            ) : (
              <div className="cs-gp-hint">
                This file looks already display-ready (Rec.709/sRGB) — keep <b>no transform</b>.
                Pick a log curve only if the footage is flat/washed-out camera log.
              </div>
            )}
          </>
        ) : null}

        {!showDisplay && stageMode && showInput ? (
          <div className="cs-gp-hint">
            Nothing to decode for display-ready footage — the Display Transform controls (log
            exposure, tone map, roll-off, output) appear when a camera log is chosen under
            <b> Input color science</b> above.
          </div>
        ) : null}
        {showDisplay ? (
          <div className="cs-gp-drt">
            <div className="cs-gp-drt-title">Display transform (IPP2)</div>
            <Slider
              label="Log exposure"
              value={disp.exposure ?? 0}
              onChange={(v) => set(['display', 'exposure'], v)}
              min={-4}
              max={5}
              step={0.05}
              def={0}
              unit="EV"
              sug={sugFor(['display', 'exposure'])}
            />
            <div className="cs-gp-hint">
              Scene-linear exposure applied <b>before</b> the tone-map — the per-clip normalization
              that puts the de-logged mid-gray at a natural level. Set this first; leaving it at 0
              is why a fresh de-log looks far too dark.
            </div>
            <Seg
              label="Tone map"
              value={disp.tonemap || 'medium'}
              options={TONE_OPTS}
              onChange={(v) => set(['display', 'tonemap'], v)}
              sug={sugFor(['display', 'tonemap'])}
              def={DEFAULT_GRADE.display.tonemap}
            />
            <Seg
              label="Highlight roll-off"
              value={disp.rolloff || 'off'}
              options={ROLL_OPTS}
              onChange={(v) => set(['display', 'rolloff'], v)}
              sug={sugFor(['display', 'rolloff'])}
              def={DEFAULT_GRADE.display.rolloff}
            />
            <Seg
              label="Output"
              value={disp.output || 'rec709'}
              options={['rec709', 'srgb']}
              onChange={(v) => set(['display', 'output'], v)}
              render={(o) => (o === 'rec709' ? 'Rec.709' : 'sRGB')}
              sug={sugFor(['display', 'output'])}
              def={DEFAULT_GRADE.display.output}
            />
            <div className="cs-gp-hint">
              How the log/wide-gamut footage is mapped to display. <b>Tone map</b> &amp;
              <b> roll-off</b> tame blown spray &amp; sun; <b>Output</b> picks the display encoding
              (Rec.709 for video, sRGB for stills bound for Lightroom).
            </div>
          </div>
        ) : null}
      </Section>
        );
      })()}

      {showAny('light', 'wb', 'exposure', 'tone') && (
      <Section
        title="Basic"
        defaultOpen
        onReset={() => resetMany([['wb', DEFAULT_GRADE.wb], ['basic', DEFAULT_GRADE.basic]])}
        canReset={!(sameJson(g.wb, DEFAULT_GRADE.wb) && sameJson(g.basic, DEFAULT_GRADE.basic))}
      >
        {showAny('light', 'wb') && <Slider label="Temp" value={g.wb.temp} onChange={(v) => set(['wb', 'temp'], v)} sug={sugFor(['wb', 'temp'])} />}
        {showAny('light', 'wb') && <Slider label="Tint" value={g.wb.tint} onChange={(v) => set(['wb', 'tint'], v)} sug={sugFor(['wb', 'tint'])} />}
        {showAny('light', 'exposure') && <Slider label="Exposure" value={g.basic.exposure} onChange={(v) => set(['basic', 'exposure'], v)} min={-5} max={5} step={0.05} unit="EV" sug={sugFor(['basic', 'exposure'])} />}
        {showAny('light', 'exposure') && <Slider label="Contrast" value={g.basic.contrast} onChange={(v) => set(['basic', 'contrast'], v)} sug={sugFor(['basic', 'contrast'])} />}
        {showAny('light', 'tone') && <Slider label="Highlights" value={g.basic.highlights} onChange={(v) => set(['basic', 'highlights'], v)} sug={sugFor(['basic', 'highlights'])} />}
        {showAny('light', 'tone') && <Slider label="Shadows" value={g.basic.shadows} onChange={(v) => set(['basic', 'shadows'], v)} sug={sugFor(['basic', 'shadows'])} />}
        {showAny('light', 'exposure') && <Slider label="Whites" value={g.basic.whites} onChange={(v) => set(['basic', 'whites'], v)} sug={sugFor(['basic', 'whites'])} />}
        {showAny('light', 'exposure') && <Slider label="Blacks" value={g.basic.blacks} onChange={(v) => set(['basic', 'blacks'], v)} sug={sugFor(['basic', 'blacks'])} />}
      </Section>
      )}

      {showAny('finish', 'detail', 'vibrance') && (
      <Section
        title="Presence"
        defaultOpen={stageMode}
        onReset={() => resetMany([['presence', DEFAULT_GRADE.presence]])}
        canReset={!sameJson(g.presence, DEFAULT_GRADE.presence)}
      >
        {showAny('finish', 'detail') && <Slider label="Texture" value={g.presence.texture} onChange={(v) => set(['presence', 'texture'], v)} sug={sugFor(['presence', 'texture'])} />}
        {showAny('finish', 'detail') && <Slider label="Clarity" value={g.presence.clarity} onChange={(v) => set(['presence', 'clarity'], v)} sug={sugFor(['presence', 'clarity'])} />}
        {showAny('finish', 'detail') && <Slider label="Dehaze" value={g.presence.dehaze} onChange={(v) => set(['presence', 'dehaze'], v)} sug={sugFor(['presence', 'dehaze'])} />}
        {showAny('finish', 'vibrance') && <Slider label="Vibrance" value={g.presence.vibrance} onChange={(v) => set(['presence', 'vibrance'], v)} sug={sugFor(['presence', 'vibrance'])} />}
        {showAny('finish', 'vibrance') && <Slider label="Saturation" value={g.presence.saturation} onChange={(v) => set(['presence', 'saturation'], v)} sug={sugFor(['presence', 'saturation'])} />}
        {showAny('finish', 'vibrance') && <Slider label="Skin protect" value={g.presence.skinProtect ?? 0} onChange={(v) => set(['presence', 'skinProtect'], v)} min={0} max={100} sug={sugFor(['presence', 'skinProtect'])} />}
        {showAny('finish', 'vibrance') && <div className="cs-gp-hint">Skin protect holds orange/skin hues back from vibrance/saturation so faces stay natural while water &amp; sky pop.</div>}
        {showAny('finish', 'detail') && <Slider label="Noise reduction" value={g.presence.denoise ?? 0} onChange={(v) => set(['presence', 'denoise'], v)} min={0} max={100} sug={sugFor(['presence', 'denoise'])} />}
        {showAny('finish', 'detail') && <div className="cs-gp-hint">Edge-preserving denoise (bilateral preview · hqdn3d on export). Runs before clarity/sharpen.</div>}
      </Section>
      )}

      {showAny('light', 'tone') && (
      <Section
        title="Tone Curve"
        defaultOpen={stageMode}
        onReset={() => resetMany([[['curve', 'master'], null]])}
        canReset={g.curve?.master != null}
      >
        <ToneCurve
          value={g.curve?.master}
          onChange={(pts) => set(['curve', 'master'], pts)}
          xBar="linear-gradient(90deg,#000,#fff)"
          axes={{
            x: { title: 'Input tone →', ticks: ['Blacks', 'Mids', 'Whites'] },
            y: { title: 'Output', ticks: ['White', 'Mid', 'Black'] },
          }}
        />
        <Sug sug={sugFor(['curve', 'master'])} current={g.curve?.master ?? null} />
        <div className="cs-gp-hint">Diagonal = no change. Lift the bottom-left to open shadows, pull the top-right to tame highlights · click to add a point · double-click to remove</div>
      </Section>
      )}

      {showAny('color', 'hsl') && (
      <Section
        title="Color Mixer (HSL)"
        defaultOpen={stageMode}
        onReset={() => resetMany([['hsl', DEFAULT_GRADE.hsl]])}
        canReset={!sameJson(g.hsl, DEFAULT_GRADE.hsl)}
      >
        <div className="cs-gp-bands">
          {BAND_NAMES.map((name, i) => (
            <button
              key={name}
              type="button"
              className={`cs-band ${band === i ? 'active' : ''}`}
              style={{ background: BAND_COLORS[i] }}
              title={name}
              onClick={() => setBand(i)}
            />
          ))}
        </div>
        <div className="cs-gp-bandname">{BAND_NAMES[band]}</div>
        <Slider label="Hue" value={g.hsl[band].h} onChange={(v) => set(['hsl', band, 'h'], v)} sug={sugFor(['hsl', band, 'h'])} />
        <Slider label="Saturation" value={g.hsl[band].s} onChange={(v) => set(['hsl', band, 's'], v)} sug={sugFor(['hsl', band, 's'])} />
        <Slider label="Luminance" value={g.hsl[band].l} onChange={(v) => set(['hsl', band, 'l'], v)} sug={sugFor(['hsl', band, 'l'])} />
      </Section>
      )}

      {showAny('color', 'curves') && (
      <Section
        title="Secondary curves (color warper)"
        defaultOpen={stageMode}
        onReset={() => resetMany([['hsCurves', DEFAULT_GRADE.hsCurves]])}
        canReset={!sameJson(g.hsCurves, DEFAULT_GRADE.hsCurves)}
      >
        <div className="cs-gp-hint">
          Resolve-style curves — flat line = no change. Drag points to qualify by hue, saturation or
          luma. Great for boosting the ocean’s blues/cyans without touching skin.
        </div>
        <div className="cs-gp-warp">
          {(() => {
            const HUE_BAR = 'linear-gradient(90deg,#f33,#fd3,#6f3,#3fd,#36f,#d3f,#f33)';
            const TONE_BAR = 'linear-gradient(90deg,#000,#fff)';
            const SAT_BAR = 'linear-gradient(90deg,#7a7a7a,#1a93ff)';
            const HUE_TICKS = ['R', 'Y', 'G', 'C', 'B', 'M', 'R'];
            return [
              ['hueSat', 'Hue → Sat', 'Boost/cut saturation per hue (push the water, hold skin)', HUE_BAR,
                { x: { title: 'Hue', ticks: HUE_TICKS }, y: { title: 'Saturation', ticks: ['+', '0', '−'] } }],
              ['hueHue', 'Hue → Hue', 'Shift hues (e.g. nudge blue toward teal)', HUE_BAR,
                { x: { title: 'Hue', ticks: HUE_TICKS }, y: { title: 'Hue shift', ticks: ['+', '0', '−'] } }],
              ['hueLum', 'Hue → Luma', 'Brighten/darken specific hues', HUE_BAR,
                { x: { title: 'Hue', ticks: HUE_TICKS }, y: { title: 'Luma', ticks: ['+', '0', '−'] } }],
              ['satSat', 'Sat → Sat', 'Lift low-sat areas, tame the already-vivid', SAT_BAR,
                { x: { title: 'Input saturation →', ticks: ['Dull', 'Mid', 'Vivid'] }, y: { title: 'Saturation', ticks: ['+', '0', '−'] } }],
              ['lumSat', 'Luma → Sat', 'Desaturate shadows / protect highlights', TONE_BAR,
                { x: { title: 'Input luma →', ticks: ['Shadows', 'Mids', 'Highs'] }, y: { title: 'Saturation', ticks: ['+', '0', '−'] } }],
            ];
          })().map(([key, label, hint, bar, axes]) => (
            <div key={key} className="cs-gp-warprow">
              <div className="cs-gp-warphead">
                <span>{label}</span>
                <button
                  type="button"
                  className="cs-btn cs-btn-sm cs-btn-ghost"
                  disabled={!g.hsCurves?.[key]}
                  title={g.hsCurves?.[key] ? `Reset ${label} to flat (no change)` : `${label} is already flat`}
                  onClick={() => set(['hsCurves', key], null)}
                >
                  Reset
                </button>
              </div>
              <ToneCurve baseline="mid" value={g.hsCurves?.[key] || null} onChange={(pts) => set(['hsCurves', key], pts)} xBar={bar} axes={axes} />
              <Sug sug={sugFor(['hsCurves', key])} current={g.hsCurves?.[key] || null} />
              <div className="cs-gp-hint">{hint}</div>
            </div>
          ))}
        </div>
      </Section>
      )}

      {showAny('color', 'wheels') && (
      <Section
        title="Color Grading"
        defaultOpen={stageMode}
        onReset={() => resetMany([['colorGrading', DEFAULT_GRADE.colorGrading]])}
        canReset={!sameJson(g.colorGrading, DEFAULT_GRADE.colorGrading)}
      >
        <div className="cs-gp-wheels">
          {[['shadows', 'Shadows'], ['midtones', 'Midtones'], ['highlights', 'Highlights'], ['global', 'Global']].map(([key, label]) => {
            const val = g.colorGrading[key] || { h: 0, s: 0, l: 0 };
            const neutral = { h: 0, s: 0, l: 0 };
            const atDef = !val.h && !val.s && !val.l;
            return (
              <div key={key} className="cs-gp-wheelcell">
                <ColorWheel label={label} value={val} onChange={(v) => set(['colorGrading', key], v)} />
                <div className="cs-gp-wheelfoot">
                  <ResetBtn atDef={atDef} onReset={() => set(['colorGrading', key], neutral)} what={`${label} wheel`} defLabel="neutral (double-click the pad also resets)" />
                  <Sug sug={sugFor(['colorGrading', key])} current={val} />
                </div>
              </div>
            );
          })}
        </div>
      </Section>
      )}

      {show('local') && (() => {
        const masks = g.masks || [];
        const water = masks.find((m) => m && m.source === 'auto-water');
        const subject = masks.find((m) => m && m.source === 'auto-subject');
        const setMask = (source, mut) => {
          onChange({
            ...g,
            masks: masks.map((m) => (m && m.source === source
              ? mut({ ...m, adj: { ...(m.adj || {}) } })
              : m)),
          });
        };
        const removeAuto = () => onChange({ ...g, masks: masks.filter((m) => !(m && typeof m.source === 'string' && m.source.startsWith('auto-'))) });
        return (
          <Section title="Local masks (auto)" defaultOpen={stageMode} onReset={removeAuto} canReset={!!(water || subject)}>
            {!water && !subject && (
              <div className="cs-gp-hint">No auto local masks on this grade. Use <b>Pull in AI masks</b> above to add the AI’s water-teal &amp; subject-lift masks, then fine-tune them here — or leave them off for a cleaner, global-only look.</div>
            )}
            {water && (
              <>
                <div className="cs-gp-bandname">Water teal pop</div>
                <Slider label="Teal saturation" value={water.adj?.saturation || 0} onChange={(v) => setMask('auto-water', (m) => { m.adj.saturation = v; return m; })} min={-50} max={50} />
                <Slider label="Strength" value={Math.round((water.amount == null ? 1 : water.amount) * 100)} onChange={(v) => setMask('auto-water', (m) => { m.amount = v / 100; return m; })} min={0} max={100} def={100} />
              </>
            )}
            {subject && (
              <>
                <div className="cs-gp-bandname">Subject shadow lift</div>
                <Slider label="Lift" value={subject.adj?.exposure || 0} onChange={(v) => setMask('auto-subject', (m) => { m.adj.exposure = v; return m; })} min={0} max={2} step={0.05} unit="EV" />
                <Slider label="Strength" value={Math.round((subject.amount == null ? 1 : subject.amount) * 100)} onChange={(v) => setMask('auto-subject', (m) => { m.amount = v / 100; return m; })} min={0} max={100} def={100} />
              </>
            )}
            <div className="cs-gp-hint">These are the AI’s scene-perception masks (water hue-key + shadowed-subject), applied on top of your global grade — the step that was previously hidden. Adjust, or reset to remove them.</div>
          </Section>
        );
      })()}

      {showAny('finish', 'detail', 'effects') && (
      <Section
        title="Effects"
        defaultOpen={stageMode}
        onReset={() => resetMany([['effects', DEFAULT_GRADE.effects]])}
        canReset={!sameJson(g.effects, DEFAULT_GRADE.effects)}
      >
        {showAny('finish', 'effects') && <Slider label="Vignette" value={g.effects.vignette} onChange={(v) => set(['effects', 'vignette'], v)} min={0} max={100} sug={sugFor(['effects', 'vignette'])} />}
        {showAny('finish', 'effects') && <Slider label="Grain" value={g.effects.grain} onChange={(v) => set(['effects', 'grain'], v)} min={0} max={100} sug={sugFor(['effects', 'grain'])} />}
        {showAny('finish', 'detail') && <Slider label="Sharpening" value={g.effects.sharpness} onChange={(v) => set(['effects', 'sharpness'], v)} min={0} max={100} sug={sugFor(['effects', 'sharpness'])} />}
        {showAny('finish', 'effects') && <Slider label="Glow" value={g.effects.glow ?? 0} onChange={(v) => set(['effects', 'glow'], v)} min={0} max={100} sug={sugFor(['effects', 'glow'])} />}
        {showAny('finish', 'effects') && <Slider label="Halation" value={g.effects.halation ?? 0} onChange={(v) => set(['effects', 'halation'], v)} min={0} max={100} sug={sugFor(['effects', 'halation'])} />}
        {showAny('finish', 'effects') && <div className="cs-gp-hint">Glow blooms highlights; Halation adds a warm film-style halo on spray &amp; sun glints.</div>}
      </Section>
      )}

      {!stageMode && presets.length && onDeletePreset ? (
        <Section title="Manage presets">
          {presets.map((p) => (
            <div key={p.id} className="cs-gp-prow">
              <span>{p.name}</span>
              <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => onDeletePreset(p.id)}>Delete</button>
            </div>
          ))}
        </Section>
      ) : null}
    </div>
  );
}
